import { useMemo, useState } from "react";
import { completedComics, latestUpdates, type ComicItem } from "@/mocks/comics";

const allComics: ComicItem[] = [...latestUpdates, ...completedComics];
const genreOptions = ["全部", ...Array.from(new Set(allComics.map((c) => c.genre)))];
const statusOptions = ["全部", "连载中", "已完结"] as const;

type StatusOption = (typeof statusOptions)[number];

export default function LatestUpdates() {
  const [status, setStatus] = useState<StatusOption>("全部");
  const [genre, setGenre] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return allComics.filter((comic) => {
      if (status !== "全部" && comic.status !== status) return false;
      if (genre !== "全部" && comic.genre !== genre) return false;
      if (!kw) return true;
      const haystack = `${comic.title} ${comic.author} ${comic.genre} ${comic.tags.join(" ")}`.toLowerCase();
      return haystack.includes(kw);
    });
  }, [status, genre, keyword]);

  return (
    <section id="latest" className="scroll-mt-28 bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1180px] px-4 md:px-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
              <i className="ri-refresh-line text-sm" aria-hidden="true"></i>
              更新与全集一览
            </span>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">最新更新 · 按状态筛选作品库</h2>
            <p className="mt-2 max-w-[640px] text-sm leading-relaxed text-foreground-600">
              当前分区收录 {allComics.length} 部代表作。切到「连载中」跟进新话，切到「已完结」直接看全集，
              也可以用关键词在你的收藏口味里找一部。
            </p>
          </div>
          <p className="text-sm text-foreground-600 whitespace-nowrap">
            筛选结果 <strong className="font-heading text-lg text-primary-600">{results.length}</strong> 部
          </p>
        </div>

        {/* filter bar */}
        <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-3 md:p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">状态</span>
              <div className="flex items-center rounded-full bg-background-200 p-1">
                {statusOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setStatus(option)}
                    aria-pressed={status === option}
                    className={`cursor-pointer rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                      status === option
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <label className="relative flex w-full items-center lg:w-72">
              <i
                className="ri-search-line pointer-events-none absolute left-3 text-base text-foreground-400"
                aria-hidden="true"
              ></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索作品名、作者或标签"
                aria-label="搜索作品"
                className="h-10 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">题材</span>
            {genreOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setGenre(option)}
                aria-pressed={genre === option}
                className={`cursor-pointer rounded-full border px-3 py-1 text-xs transition-colors whitespace-nowrap ${
                  genre === option
                    ? "border-secondary-500 bg-secondary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:border-secondary-300 hover:text-secondary-900"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* results */}
        {results.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((comic) => (
              <article
                key={comic.id}
                className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
              >
                <div className="h-[132px] w-[94px] shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={comic.cover}
                    alt={`《${comic.title}》封面插画`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="truncate font-heading text-base text-foreground-950">{comic.title}</h3>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ${
                        comic.status === "已完结"
                          ? "bg-accent-100 text-accent-900"
                          : "bg-secondary-100 text-secondary-900"
                      }`}
                    >
                      {comic.status}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-foreground-500">
                    {comic.author} · {comic.genre}
                  </p>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{comic.summary}</p>
                  <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 text-[11px] text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-book-marked-line" aria-hidden="true"></i>
                      {comic.progress}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-time-line" aria-hidden="true"></i>
                      {comic.updatedAt}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-fire-line" aria-hidden="true"></i>
                      {comic.heat}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
            <i className="ri-emotion-unhappy-line text-3xl text-foreground-400" aria-hidden="true"></i>
            <p className="mt-3 text-sm text-foreground-700">没有符合当前条件的作品</p>
            <p className="mt-1 text-xs text-foreground-500">试着换一个状态或题材，或者清空关键词再找找看。</p>
            <button
              type="button"
              onClick={() => {
                setStatus("全部");
                setGenre("全部");
                setKeyword("");
              }}
              className="mt-4 inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-restart-line text-base" aria-hidden="true"></i>
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}